import { Link } from "react-router-dom";
import { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "compact" | "featured";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
          <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover" title={article.title} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 leading-snug">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span>{article.publishedAt}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
      >
        <div className="aspect-[16/9] overflow-hidden bg-background-200">
          <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" title={article.title} />
        </div>
        <div className="p-4">
          <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700 mb-2">
            {article.categoryName}
          </span>
          <h3 className="text-base font-heading font-bold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 leading-snug">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.summary}</p>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
            <span>{article.publishedAt}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {article.comments}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
    >
      <div className="aspect-[16/10] overflow-hidden bg-background-200">
        <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" title={article.title} />
      </div>
      <div className="p-4">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-accent-100 text-accent-700 mb-2">
          {article.categoryName}
        </span>
        <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <p className="mt-2 text-xs text-foreground-600 line-clamp-2 leading-relaxed">{article.summary}</p>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
          <span>{article.publishedAt}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
          </span>
        </div>
      </div>
    </Link>
  );
}